import { Box, SxProps, TableBody, TableCell, TableHead, TableRow, Tooltip, useTheme } from "@mui/material";
import { Deriv } from "../../../interfaces/dtos/deriv";
import { RPMTableHeaderRow } from "../../shared/table/RPMTableHeaderRow";
import { RPMTableHeaderCell } from "../../shared/table/RPMTableHeaderCell";
import { RPMTableScrollable } from "../../shared/table/RPMTableScrollable";
import { useAppSelector } from "../../../redux/hooks";
import { rdsState } from "../../../redux/rds/rdsSlice";
import { commonColours } from "../../shared/commonConstants";
import { rdsSelection_Dto, rdsSelectionStatuses } from "../../../interfaces/dtos/rdsSelection";
import { suspendedText } from "./ScheduleDerivsList";
import dayjs from "dayjs";

interface ScheduleDerivsListItemProps {
    eventDerivs: Deriv[];
}

export const ScheduleDerivsListItem = ({
    eventDerivs
}: ScheduleDerivsListItemProps) => {

    const theme = useTheme();
    const rds = useAppSelector(rdsState);

    if (eventDerivs.length === 0) return <></>

    const selections = Array.prototype.concat(...eventDerivs.map(ed => ed.calculated?.selections));
    const distinctSelections = [...new Set(selections.map(s => s.selectionKey))];

    const getSelection = (deriv: Deriv, selection: string) => {
        return deriv.calculated?.selections?.find(s => s.selectionKey === selection);
    }

    const getSelectionDescription = (derivs: Deriv[], selection: string, iteration: number) => {
        const currentSelection = getSelection(derivs[0], selection);
        if (currentSelection?.showRunnerInfo) {
            if (currentSelection.selections!.length > 1) {
                return currentSelection.selections!.map(r => getRunnerNumber(r)).join(", ");
            } else if (currentSelection.selections!.length === 1) {
                const runner = rds.rdsRunners.find(r => r.id.toString() === currentSelection.selections![0]);
                const selection = rds.rdsSelections.find(s => s.id.toString() === currentSelection.selections![0]);
                return `${selection?.number}. ${runner?.name} ${getSelectionDescriptionStatus(selection)}`;
            } else {
                return "";
            }
        } else {
            return currentSelection?.selections && currentSelection.selections?.length > iteration ?
                currentSelection.selections[iteration] : "";
        }
    }

    const getSelectionDescriptionStatus = (rdsSelection?: rdsSelection_Dto) => {
        switch (rdsSelection?.status) {
            case rdsSelectionStatuses.NonRunner: return "(N/R)";
            case rdsSelectionStatuses.Withdrawn: return "(W/D)";
            case rdsSelectionStatuses.Vacant: return "(Vac)";
            case rdsSelectionStatuses.Reserve: return "(Res)";
            default: return "";
        }
    }

    const getRunnerNumber = (runnerId: string) => {
        const selection = rds.rdsSelections.find(s => s.id.toString() === runnerId);
        return selection?.number;
    }

    const getOverround = (deriv: Deriv) => {
        const total = deriv.calculated?.selections.reduce((partialSum, a) => partialSum + a.overround, 0) ?? 0;
        return `${Number(total.toFixed(2))}%`;
    }

    const colStickeySx: SxProps = {
        position: "sticky",
        textWrap: 'nowrap',
        background: theme.palette.grey[200],
    }

    const col1Width = 110;

    const colHeaderCommon = { fontWeight: 'bold' }

    const col1HeaderSx = { ...colStickeySx, ...colHeaderCommon, left: 0, width: col1Width, zIndex: 6 };
    const col1Sx = { ...colStickeySx, left: 0, width: col1Width, whiteSpace: 'nowrap' };

    const getIsSuspended = (eventDeriv: Deriv) => { return eventDeriv.calculated?.isSuspended; }
    const itemColour = (eventDeriv: Deriv) => getIsSuspended(eventDeriv) ? commonColours(theme).suspended : theme.palette.success.dark;
    const backgroundColour = (eventDeriv: Deriv) => getIsSuspended(eventDeriv) ? commonColours(theme).suspendedBackground : undefined;
    const backgroundHeaderColour = (eventDeriv: Deriv) => getIsSuspended(eventDeriv) ? commonColours(theme).suspendedHeaderBackground : theme.palette.grey[200];

    const getHeadingDesc = (eventDeriv: Deriv) => {
        const tooltip = getIsSuspended(eventDeriv) ? eventDeriv.calculated?.suspendedReason : undefined;
        const time = eventDeriv.timestamp ? dayjs(eventDeriv.timestamp).format('HH:mm:ss') : "";
        return <>
            {time}
            <Tooltip title={tooltip}>
                <Box style={{ fontSize: '12px', cursor: 'default' }}>
                    {eventDeriv.calculated?.isSuspended ? suspendedText : eventDeriv.marketStatus}
                </Box>
            </Tooltip>
        </>
    }

    return (
        <RPMTableScrollable
            id="table-derivs-items"
            containerSxProps={{ marginTop: '20px' }}
            tableProps={{ stickyHeader: true }}
        >
            <TableHead>
                <RPMTableHeaderRow>
                    <RPMTableHeaderCell sx={{ ...col1HeaderSx, color: itemColour(eventDerivs[0]) }}>
                        {eventDerivs[0].marketName}
                    </RPMTableHeaderCell>
                    {eventDerivs.map((eventDeriv, i) => (
                        <RPMTableHeaderCell
                            key={`${eventDeriv.version}_${i}`}
                            align="center" sx={colHeaderCommon}
                            style={{ color: itemColour(eventDeriv), backgroundColor: backgroundHeaderColour(eventDeriv) }}
                        >
                            {getHeadingDesc(eventDeriv)}
                        </RPMTableHeaderCell>
                    ))}
                </RPMTableHeaderRow>
            </TableHead>
            <TableBody>
                {distinctSelections.map((selection, i) => (
                    <TableRow key={selection}>
                        <TableCell sx={{ ...col1Sx }}>
                            {getSelectionDescription(eventDerivs, selection, i)}
                        </TableCell>
                        {eventDerivs.map((eventDeriv, i) => (
                            <TableCell
                                key={`${eventDeriv.version}_${i}`}
                                sx={{ color: itemColour(eventDeriv), backgroundColor: backgroundColour(eventDeriv) }}
                                align="center"
                            >
                                {getSelection(eventDeriv, selection)?.priceFract}
                            </TableCell>
                        ))}
                    </TableRow>
                ))}
                <TableRow>
                    <TableCell sx={{ ...col1Sx, fontWeight: 'bold' }}>Overround</TableCell>
                    {eventDerivs.map((eventDeriv, i) => (
                        <TableCell
                            key={`${eventDeriv.version}_${i}`}
                            sx={{ color: itemColour(eventDeriv), backgroundColor: backgroundColour(eventDeriv), fontWeight: 'bold' }}
                            align="center"
                        >
                            {getOverround(eventDeriv)}
                        </TableCell>
                    ))}
                </TableRow>
            </TableBody>
        </RPMTableScrollable>
    )
}
